import { signal, Signal, WritableSignal } from '@angular/core';
import { ImageMask } from 'src/app/interfaces/api/model';

export interface ImageMaskRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageMaskEditorOptions {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  containerElement: HTMLElement;
  onStateChange?: () => void;
  initialMasks?: ImageMask[];
}

export interface MaskImageDataLike {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

interface PixelPoint {
  x: number;
  y: number;
}

export class ImageMaskEditor {
  private readonly modeSignal: WritableSignal<"delete" | "keep"> = signal('delete');
  public readonly mode: Signal<"delete" | "keep"> = this.modeSignal.asReadonly();

  private readonly canUndoSignal: WritableSignal<boolean> = signal(false);
  public readonly canUndo: Signal<boolean> = this.canUndoSignal.asReadonly();

  private displayCanvas: HTMLCanvasElement | null = null;
  private displayCtx: CanvasRenderingContext2D | null = null;
  private sourceCanvas: HTMLCanvasElement | null = null;
  private sourceCtx: CanvasRenderingContext2D | null = null;
  private image: HTMLImageElement | null = null;
  private containerElement: HTMLElement | null = null;

  private masks: ImageMask[] = [];
  private imageUrl = '';
  private imageWidth = 0;
  private imageHeight = 0;
  private isEnabled = false;

  private imgOffsetX = 0;
  private imgOffsetY = 0;
  private zoomLevel = 1;
  private minZoom = 1;
  private readonly MAX_ZOOM = 4;
  private readonly ZOOM_STEP = 0.2;
  private readonly PADDING_RATIO = 0.2;
  private zoomTimeout: ReturnType<typeof setTimeout> | null = null;

  private isPanning = false;
  private isSelecting = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartOffsetX = 0;
  private dragStartOffsetY = 0;
  private offsetX = 0;
  private offsetY = 0;
  private selectionStart: PixelPoint | null = null;
  private currentSelection: ImageMaskRect | null = null;
  private onStateChange: (() => void) | null = null;

  private readonly onMouseDownBound = (event: MouseEvent) => this.onCanvasMouseDown(event);
  private readonly onMouseMoveBound = (event: MouseEvent) => this.onCanvasMouseMove(event);
  private readonly onMouseUpBound = (event: MouseEvent) => this.onCanvasMouseUp(event);
  private readonly onMouseLeaveBound = (event: MouseEvent) => this.onCanvasMouseLeave(event);
  private readonly onWheelBound = (event: WheelEvent) => this.onCanvasWheel(event);
  private readonly onContextMenuBound = (event: Event) => event.preventDefault();
  private readonly onResizeBound = () => this.onWindowResize();

  constructor(options: ImageMaskEditorOptions) {
    this.imageUrl = options.imageUrl;
    this.imageWidth = options.imageWidth;
    this.imageHeight = options.imageHeight;
    this.containerElement = options.containerElement;
    this.onStateChange = options.onStateChange || null;

    if (options.initialMasks?.length) {
      this.masks = options.initialMasks.map(m => ({ ...m, rect: { ...m.rect } }));
    }
  }

  async enable(): Promise<void> {
    if (this.isEnabled) return;

    await this.loadImage();
    this.createSourceCanvas();

    if (this.masks.length) {
      this.applyMasksToCanvas();
      this.canUndoSignal.set(true);
    }

    this.createDisplayCanvas();
    this.setupEventListeners();
    this.draw();
    this.isEnabled = true;
  }

  destroy(): void {
    this.cleanup();
    this.image = null;
    this.containerElement = null;
    this.onStateChange = null;
  }

  setMode(mode: "delete" | "keep"): void {
    this.modeSignal.set(mode);
  }

  undo(): void {
    if (!this.masks.length) return;

    this.masks.pop();
    this.applyMasksToCanvas();

    this.canUndoSignal.set(this.masks.length > 0);
    this.emitStateChange();
  }

  reset(): void {
    this.masks = [];
    this.applyMasksToCanvas();
    this.emitStateChange();
  }

  toDataUrl(): string {
    if (!this.sourceCanvas) return '';
    return this.sourceCanvas.toDataURL('image/png');
  }

  static applyMaskToImageData(imageData: MaskImageDataLike, rect: ImageMaskRect, mode: "delete" | "keep"): MaskImageDataLike {
    const normalized = ImageMaskEditor.normalizeRect(rect, imageData.width, imageData.height);
    const data = new Uint8ClampedArray(imageData.data);

    for (let y = 0; y < imageData.height; y++) {
      for (let x = 0; x < imageData.width; x++) {
        const inside = x >= normalized.x
          && x < normalized.x + normalized.width
          && y >= normalized.y
          && y < normalized.y + normalized.height;

        if ((mode === 'delete' && inside) || (mode === 'keep' && !inside)) {
          data[(y * imageData.width + x) * 4 + 3] = 0;
        }
      }
    }

    return {
      width: imageData.width,
      height: imageData.height,
      data,
    };
  }

  private applyCurrentSelection(): void {
    if (!this.currentSelection || !this.sourceCtx) return;

    const rect = ImageMaskEditor.normalizeRect(this.currentSelection, this.imageWidth, this.imageHeight);
    if (rect.width < 1 || rect.height < 1) {
      this.currentSelection = null;
      this.draw();
      return;
    }

    this.masks.push({
      rect,
      mode: this.modeSignal()
    });

    this.canUndoSignal.set(this.masks.length > 0);

    this.applyMasksToCanvas();

    this.currentSelection = null;
    this.draw();
    this.emitStateChange();
  }

  private createSourceCanvas(): void {
    if (!this.image) return;

    this.sourceCanvas = document.createElement('canvas');
    this.sourceCanvas.width = this.imageWidth;
    this.sourceCanvas.height = this.imageHeight;
    this.sourceCtx = this.sourceCanvas.getContext('2d');

    if (!this.sourceCtx) return;
    this.sourceCtx.drawImage(this.image, 0, 0, this.imageWidth, this.imageHeight);
  }

  private createDisplayCanvas(): void {
    if (!this.containerElement) return;

    this.displayCanvas = document.createElement('canvas');
    this.displayCanvas.id = 'image-mask-editor-canvas';
    this.displayCanvas.width = this.containerElement.clientWidth;
    this.displayCanvas.height = this.containerElement.clientHeight;
    this.displayCanvas.style.position = 'absolute';
    this.displayCanvas.style.top = '0';
    this.displayCanvas.style.left = '0';
    this.displayCanvas.style.cursor = 'crosshair';
    this.displayCanvas.style.zIndex = '99';

    this.containerElement.style.position = 'relative';
    this.containerElement.appendChild(this.displayCanvas);
    this.displayCtx = this.displayCanvas.getContext('2d');

    const paddingWidth = this.imageWidth * this.PADDING_RATIO;
    const paddingHeight = this.imageHeight * this.PADDING_RATIO;
    const totalImageWidth = this.imageWidth + (paddingWidth * 2);
    const totalImageHeight = this.imageHeight + (paddingHeight * 2);

    const ratioX = this.displayCanvas.width / totalImageWidth;
    const ratioY = this.displayCanvas.height / totalImageHeight;
    this.minZoom = Math.min(ratioX, ratioY);
    this.zoomLevel = this.minZoom;
    this.offsetX = 0;
    this.offsetY = 0;
  }

  private loadImage(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.image = new Image();
      this.image.crossOrigin = 'anonymous';
      this.image.onload = () => resolve();
      this.image.onerror = () => reject(new Error('Impossible de charger l\'image'));
      this.image.src = this.imageUrl;
    });
  }

  private setupEventListeners(): void {
    if (!this.displayCanvas) return;

    this.displayCanvas.addEventListener('mousedown', this.onMouseDownBound);
    this.displayCanvas.addEventListener('mousemove', this.onMouseMoveBound);
    this.displayCanvas.addEventListener('mouseup', this.onMouseUpBound);
    this.displayCanvas.addEventListener('mouseleave', this.onMouseLeaveBound);
    this.displayCanvas.addEventListener('wheel', this.onWheelBound, { passive: false });
    this.displayCanvas.addEventListener('contextmenu', this.onContextMenuBound);
    window.addEventListener('resize', this.onResizeBound);
  }

  private onCanvasMouseDown(event: MouseEvent): void {
    if (!this.displayCanvas) return;

    if (event.button === 2) {
      this.isPanning = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartOffsetX = this.offsetX;
      this.dragStartOffsetY = this.offsetY;
      this.displayCanvas.style.cursor = 'grabbing';
      return;
    }

    if (event.button !== 0) return;

    const point = this.getImagePointFromEvent(event);
    if (!point) return;

    this.isSelecting = true;
    this.selectionStart = point;
    this.currentSelection = { x: point.x, y: point.y, width: 0, height: 0 };
    this.draw();
  }

  private onCanvasMouseMove(event: MouseEvent): void {
    if (!this.displayCanvas) return;

    if (this.isPanning) {
      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;
      const clamped = this.clampOffsets(this.dragStartOffsetX + deltaX, this.dragStartOffsetY + deltaY);
      this.offsetX = clamped.x;
      this.offsetY = clamped.y;
      this.draw();
      return;
    }

    if (!this.isSelecting || !this.selectionStart) return;

    const point = this.getClampedImagePointFromEvent(event);
    if (!point) return;

    this.currentSelection = {
      x: Math.min(this.selectionStart.x, point.x),
      y: Math.min(this.selectionStart.y, point.y),
      width: Math.abs(point.x - this.selectionStart.x),
      height: Math.abs(point.y - this.selectionStart.y),
    };

    this.draw();
  }

  private onCanvasMouseUp(event: MouseEvent): void {
    if (event.button === 2) {
      this.isPanning = false;
      if (this.displayCanvas) this.displayCanvas.style.cursor = 'crosshair';
      return;
    }

    if (event.button !== 0 || !this.isSelecting) return;

    this.isSelecting = false;
    this.selectionStart = null;
    this.applyCurrentSelection();
  }

  private onCanvasMouseLeave(_event: MouseEvent): void {
    this.isPanning = false;

    if (this.displayCanvas) {
      this.displayCanvas.style.cursor = 'crosshair';
    }

    if (this.isSelecting) {
      this.isSelecting = false;
      this.selectionStart = null;
      this.applyCurrentSelection();
    }
  }

  private onCanvasWheel(event: WheelEvent): void {
    event.preventDefault();

    if (this.zoomTimeout) return;
    this.zoomTimeout = setTimeout(() => this.zoomTimeout = null, 16);

    const currentZoom = this.zoomLevel;
    const delta = event.deltaY > 0 ? -this.ZOOM_STEP : this.ZOOM_STEP;
    const newZoom = Math.max(this.minZoom, Math.min(this.MAX_ZOOM, currentZoom + delta));
    if (newZoom === currentZoom || !this.displayCanvas) return;

    const rect = this.displayCanvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const imgX = (this.displayCanvas.width - this.imageWidth * currentZoom) / 2 + this.offsetX;
    const imgY = (this.displayCanvas.height - this.imageHeight * currentZoom) / 2 + this.offsetY;
    const imageCoordX = (mouseX - imgX) / currentZoom;
    const imageCoordY = (mouseY - imgY) / currentZoom;
    const newImgX = mouseX - imageCoordX * newZoom;
    const newImgY = mouseY - imageCoordY * newZoom;

    this.zoomLevel = newZoom;
    const clamped = this.clampOffsets(
      newImgX - (this.displayCanvas.width - this.imageWidth * newZoom) / 2,
      newImgY - (this.displayCanvas.height - this.imageHeight * newZoom) / 2
    );

    this.offsetX = clamped.x;
    this.offsetY = clamped.y;
    this.draw();
  }

  private onWindowResize(): void {
    if (!this.containerElement || !this.displayCanvas) return;

    const newWidth = this.containerElement.clientWidth;
    const newHeight = this.containerElement.clientHeight;
    if (newWidth === this.displayCanvas.width && newHeight === this.displayCanvas.height) return;

    this.displayCanvas.width = newWidth;
    this.displayCanvas.height = newHeight;

    const paddingWidth = this.imageWidth * this.PADDING_RATIO;
    const paddingHeight = this.imageHeight * this.PADDING_RATIO;
    const totalImageWidth = this.imageWidth + (paddingWidth * 2);
    const totalImageHeight = this.imageHeight + (paddingHeight * 2);

    const ratioX = this.displayCanvas.width / totalImageWidth;
    const ratioY = this.displayCanvas.height / totalImageHeight;
    this.minZoom = Math.min(ratioX, ratioY);
    this.zoomLevel = Math.max(this.zoomLevel, this.minZoom);

    const clamped = this.clampOffsets(this.offsetX, this.offsetY);
    this.offsetX = clamped.x;
    this.offsetY = clamped.y;
    this.draw();
  }

  private draw(): void {
    if (!this.displayCtx || !this.displayCanvas || !this.sourceCanvas) return;

    this.displayCtx.clearRect(0, 0, this.displayCanvas.width, this.displayCanvas.height);
    this.drawCheckerboard();

    const scaledWidth = this.imageWidth * this.zoomLevel;
    const scaledHeight = this.imageHeight * this.zoomLevel;

    const paddingWidth = this.imageWidth * this.PADDING_RATIO * this.zoomLevel;
    const paddingHeight = this.imageHeight * this.PADDING_RATIO * this.zoomLevel;
    const totalScaledWidth = scaledWidth + (paddingWidth * 2);
    const totalScaledHeight = scaledHeight + (paddingHeight * 2);

    this.imgOffsetX = (this.displayCanvas.width - totalScaledWidth) / 2 + this.offsetX + paddingWidth;
    this.imgOffsetY = (this.displayCanvas.height - totalScaledHeight) / 2 + this.offsetY + paddingHeight;

    this.displayCtx.drawImage(this.sourceCanvas, this.imgOffsetX, this.imgOffsetY, scaledWidth, scaledHeight);
    this.drawImageBorder();
    this.drawSelection();
  }

  private drawCheckerboard(): void {
    if (!this.displayCtx || !this.displayCanvas) return;

    const size = 16;
    this.displayCtx.fillStyle = '#f6f7f8';
    this.displayCtx.fillRect(0, 0, this.displayCanvas.width, this.displayCanvas.height);

    for (let y = 0; y < this.displayCanvas.height; y += size) {
      for (let x = 0; x < this.displayCanvas.width; x += size) {
        if ((x / size + y / size) % 2 === 0) {
          this.displayCtx.fillStyle = '#e0e3e7';
          this.displayCtx.fillRect(x, y, size, size);
        }
      }
    }
  }

  private drawSelection(): void {
    if (!this.displayCtx || !this.currentSelection) return;

    const rect = ImageMaskEditor.normalizeRect(this.currentSelection, this.imageWidth, this.imageHeight);
    const x = this.imgOffsetX + rect.x * this.zoomLevel;
    const y = this.imgOffsetY + rect.y * this.zoomLevel;
    const width = rect.width * this.zoomLevel;
    const height = rect.height * this.zoomLevel;

    this.displayCtx.fillStyle = this.modeSignal() === 'delete'
      ? 'rgba(220, 53, 69, 0.22)'
      : 'rgba(40, 167, 69, 0.22)';
    this.displayCtx.strokeStyle = this.modeSignal() === 'delete'
      ? '#dc3545'
      : '#28a745';
    this.displayCtx.lineWidth = 2;
    this.displayCtx.fillRect(x, y, width, height);
    this.displayCtx.strokeRect(x, y, width, height);
  }

  private getImagePointFromEvent(event: MouseEvent): PixelPoint | null {
    if (!this.displayCanvas) return null;

    const rect = this.displayCanvas.getBoundingClientRect();
    const canvasX = event.clientX - rect.left;
    const canvasY = event.clientY - rect.top;
    const imageX = (canvasX - this.imgOffsetX) / this.zoomLevel;
    const imageY = (canvasY - this.imgOffsetY) / this.zoomLevel;

    if (imageX < 0 || imageX > this.imageWidth || imageY < 0 || imageY > this.imageHeight) {
      return null;
    }

    return {
      x: Math.max(0, Math.min(this.imageWidth, imageX)),
      y: Math.max(0, Math.min(this.imageHeight, imageY)),
    };
  }

  private getClampedImagePointFromEvent(event: MouseEvent): PixelPoint | null {
    if (!this.displayCanvas) return null;

    const rect = this.displayCanvas.getBoundingClientRect();
    const canvasX = event.clientX - rect.left;
    const canvasY = event.clientY - rect.top;
    const imageX = (canvasX - this.imgOffsetX) / this.zoomLevel;
    const imageY = (canvasY - this.imgOffsetY) / this.zoomLevel;

    return {
      x: Math.max(0, Math.min(this.imageWidth, imageX)),
      y: Math.max(0, Math.min(this.imageHeight, imageY)),
    };
  }

  private clampOffsets(offsetX: number, offsetY: number): PixelPoint {
    if (!this.displayCanvas) return { x: offsetX, y: offsetY };

    const scaledWidth = this.imageWidth * this.zoomLevel;
    const scaledHeight = this.imageHeight * this.zoomLevel;

    const paddingWidth = this.imageWidth * this.PADDING_RATIO * this.zoomLevel;
    const paddingHeight = this.imageHeight * this.PADDING_RATIO * this.zoomLevel;
    const totalScaledWidth = scaledWidth + (paddingWidth * 2);
    const totalScaledHeight = scaledHeight + (paddingHeight * 2);

    const maxOffsetX = Math.max(0, (totalScaledWidth - this.displayCanvas.width) / 2);
    const maxOffsetY = Math.max(0, (totalScaledHeight - this.displayCanvas.height) / 2);

    return {
      x: Math.max(-maxOffsetX, Math.min(maxOffsetX, offsetX)),
      y: Math.max(-maxOffsetY, Math.min(maxOffsetY, offsetY)),
    };
  }

  private emitStateChange(): void {
    this.onStateChange?.();
  }

  private cleanup(): void {
    if (this.displayCanvas) {
      this.displayCanvas.removeEventListener('mousedown', this.onMouseDownBound);
      this.displayCanvas.removeEventListener('mousemove', this.onMouseMoveBound);
      this.displayCanvas.removeEventListener('mouseup', this.onMouseUpBound);
      this.displayCanvas.removeEventListener('mouseleave', this.onMouseLeaveBound);
      this.displayCanvas.removeEventListener('wheel', this.onWheelBound);
      this.displayCanvas.removeEventListener('contextmenu', this.onContextMenuBound);
      this.displayCanvas.parentElement?.removeChild(this.displayCanvas);
    }

    window.removeEventListener('resize', this.onResizeBound);

    this.displayCanvas = null;
    this.displayCtx = null;
    this.sourceCanvas = null;
    this.sourceCtx = null;
    this.isEnabled = false;
  }

  private static normalizeRect(rect: ImageMaskRect, imageWidth: number, imageHeight: number): ImageMaskRect {
    const x = Math.max(0, Math.min(imageWidth, Math.floor(rect.x)));
    const y = Math.max(0, Math.min(imageHeight, Math.floor(rect.y)));
    const right = Math.max(0, Math.min(imageWidth, Math.ceil(rect.x + rect.width)));
    const bottom = Math.max(0, Math.min(imageHeight, Math.ceil(rect.y + rect.height)));

    return {
      x,
      y,
      width: Math.max(0, right - x),
      height: Math.max(0, bottom - y),
    };
  }

  private applyMasksToCanvas(): void {
    if (!this.sourceCtx || !this.image) return;

    this.sourceCtx.clearRect(0, 0, this.imageWidth, this.imageHeight);
    this.sourceCtx.drawImage(this.image, 0, 0, this.imageWidth, this.imageHeight);

    if (!this.masks.length) {
      this.draw();
      return;
    }

    const imageData = this.sourceCtx.getImageData(0, 0, this.imageWidth, this.imageHeight);
    ImageMaskEditor.applyMasksToImageData(imageData, this.masks, this.imageWidth, this.imageHeight);
    this.sourceCtx.putImageData(imageData, 0, 0);
    this.draw();
  }

  getMasks(): ImageMask[] {
    return structuredClone(this.masks);
  }

  private drawImageBorder(): void {
    if (!this.displayCtx) return;

    const scaledWidth = this.imageWidth * this.zoomLevel;
    const scaledHeight = this.imageHeight * this.zoomLevel;

    this.displayCtx.strokeStyle = '#999999';
    this.displayCtx.lineWidth = 2;
    this.displayCtx.setLineDash([5, 5]);

    this.displayCtx.strokeRect(
      this.imgOffsetX,
      this.imgOffsetY,
      scaledWidth,
      scaledHeight
    );

    this.displayCtx.setLineDash([]);
  }

  public static applyMasksToImageData(imageData: ImageData, masks: ImageMask[], imageWidth: number, imageHeight: number): void {
    const data = imageData.data;

    for (const mask of masks) {
      const rect = ImageMaskEditor.normalizeRect(mask.rect, imageWidth, imageHeight);

      if (mask.mode === 'delete') {
        for (let y = rect.y; y < rect.y + rect.height; y++) {
          for (let x = rect.x; x < rect.x + rect.width; x++) {
            data[(y * imageWidth + x) * 4 + 3] = 0;
          }
        }
      }

      if (mask.mode === 'keep') {
        for (let y = 0; y < imageHeight; y++) {
          for (let x = 0; x < imageWidth; x++) {
            const inside =
              x >= rect.x && x < rect.x + rect.width &&
              y >= rect.y && y < rect.y + rect.height;

            if (!inside) {
              data[(y * imageWidth + x) * 4 + 3] = 0;
            }
          }
        }
      }
    }
  }
}
